import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import useIsMobile from '@/hooks/useIsMobile'
import { theme } from '@/lib/theme'
import { api } from '@/lib/api/client'
import Button from './ui/Button'
import Input from './ui/Input'
import Message from './ui/Message'
import TOSModal from './auth/TOSModal'

interface AccountInfoProps {
  onClose: () => void
}

/**
 * Account Info popup component for managing user account details.
 */
export default function AccountInfo({ onClose }: AccountInfoProps) {
  const { colors } = useThemeMode()
  const { user } = useUser()
  const isMobile = useIsMobile()
  const [isLoading, setIsLoading] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null)
  const [showTOSModal, setShowTOSModal] = useState(false)

  // Get font mode from localStorage to match the app's font setting
  const fontMode = typeof window !== 'undefined'
    ? (localStorage.getItem('kyogre-font-mode') as 'departure-mono' | 'open-dyslexic' | 'system' || 'departure-mono')
    : 'departure-mono'

  // Get email from Supabase user
  const [email, setEmail] = useState(user?.email || '')
  const [emailKey, setEmailKey] = useState(0)

  // Update email when user changes
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email)
    }
  }, [user])
  const [isEditingEmail, setIsEditingEmail] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const handleSaveEmail = async () => {
    setIsLoading(true)
    setFeedbackMessage(null)

    try {
      if (email === user?.email) {
        setIsEditingEmail(false)
        setIsLoading(false)
        return
      }

      const { data, error } = await api.auth['update-email'].post({ email })

      if (error) {
        const errorMessage = typeof error === 'string' ? error : JSON.stringify(error)
        throw new Error(errorMessage)
      }

      // Update stored user data with new email
      if (data?.user) {
        const storedUser = localStorage.getItem('user')
        if (storedUser) {
          const userData = JSON.parse(storedUser)
          userData.email = email
          localStorage.setItem('user', JSON.stringify(userData))
        }
      }

      setIsEditingEmail(false)
      setEmailKey(prev => prev + 1) // Trigger animation
      setFeedbackMessage('Email updated successfully!')

      // Auto-close the message after 3 seconds
      setTimeout(() => {
        setFeedbackMessage(null)
      }, 3000)
    } catch (error: any) {
      const errorMessage = error?.message || 'Error updating email'
      setFeedbackMessage(`Error: ${errorMessage}`)
      console.error('Email update error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSavePassword = async () => {
    setIsLoading(true)
    setFeedbackMessage(null)

    try {
      // Validate password fields
      if (!currentPassword) {
        setFeedbackMessage('Error: Please enter your current password')
        setIsLoading(false)
        return
      }
      if (!newPassword || !confirmPassword) {
        setFeedbackMessage('Error: Please enter and confirm your new password')
        setIsLoading(false)
        return
      }
      if (newPassword !== confirmPassword) {
        setFeedbackMessage('Error: New passwords do not match')
        setIsLoading(false)
        return
      }
      if (newPassword.length < 8) {
        setFeedbackMessage('Error: Password must be at least 8 characters')
        setIsLoading(false)
        return
      }

      const { data, error } = await api.auth['update-password'].post({
          current_password: currentPassword,
          password: newPassword,
        })

      if (error) {
        const errorMessage = typeof error === 'string' ? error : JSON.stringify(error)
        throw new Error(errorMessage)
      }

      // Clear password fields and exit password change mode
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setIsChangingPassword(false)
      setFeedbackMessage('Password updated successfully!')

      // Auto-close the message after 3 seconds
      setTimeout(() => {
        setFeedbackMessage(null)
      }, 3000)
    } catch (error: any) {
      const errorMessage = error?.message || 'Error updating password'
      setFeedbackMessage(`Error: ${errorMessage}`)
      console.error('Password update error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '40px',
        backdropFilter: 'blur(8px)',
        overflow: 'auto',
        fontFamily:
          fontMode === 'departure-mono'
            ? 'var(--font-departure-mono), monospace'
            : fontMode === 'open-dyslexic'
              ? 'var(--font-open-dyslexic), sans-serif'
              : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault() // Prevent default form submission
        }}
        style={{
          backgroundColor: colors.primaryBg,
          borderRadius: isMobile ? '8px' : '10px',
          border: `${isMobile ? '1px' : '2px'} solid ${colors.primaryBorder}`,
          padding: isMobile ? '14px' : '24px',
          maxWidth: '500px',
          width: isMobile ? '95%' : '90%',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05)',
          margin: 'auto',
        }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: isMobile ? '10px' : '16px',
            right: isMobile ? '10px' : '16px',
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            color: colors.primaryText,
            cursor: 'pointer',
            fontSize: isMobile ? '14px' : '18px',
            padding: isMobile ? '4px 8px' : '6px 10px',
            lineHeight: '1',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.primaryBorder
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.secondaryBg
          }}
        >
          ✕
        </button>

        {/* Header */}
        <h2
          style={{
            fontSize: isMobile ? '14px' : '22px',
            fontWeight: '700',
            marginBottom: isMobile ? '12px' : '20px',
            color: colors.primaryText,
            letterSpacing: '-0.3px',
            paddingRight: '40px',
          }}
        >
          👤 Account Information
        </h2>

      {feedbackMessage && (
        <Message
          variant={feedbackMessage.startsWith('Error') ? 'error' : 'success'}
          style={{ marginBottom: theme.spacing.lg }}
        >
          {feedbackMessage}
        </Message>
      )}

      {/* Email Section */}
      <div
        style={{
          backgroundColor: colors.secondaryBg,
          border: `${isMobile ? '1px' : '2px'} solid ${colors.primaryBorder}`,
          borderRadius: isMobile ? '6px' : '8px',
          padding: isMobile ? '12px' : '18px',
          marginBottom: isMobile ? '10px' : '16px',
        }}
      >
        {isEditingEmail ? (
          <>
            <Input
              id="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
              placeholder="Enter your new email"
              disabled={isLoading}
              type="email"
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '10px',
              }}
            >
              <Button
                type="button"
                onClick={() => {
                  setIsEditingEmail(false)
                  setEmail(user?.email || '')
                }}
                variant="secondary"
                size="md"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveEmail}
                disabled={isLoading}
                isLoading={isLoading}
                variant="primary"
                size="md"
              >
                Save
              </Button>
            </div>
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? '12px' : '16px',
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: isMobile ? '11px' : '13px',
                  fontWeight: '600',
                  color: colors.secondaryText,
                  marginBottom: '4px',
                }}
              >
                Email Address
              </label>
              <AnimatePresence mode="wait">
                <motion.div
                  key={emailKey}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    fontSize: isMobile ? '13px' : '15px',
                    color: colors.primaryText,
                    fontWeight: '500',
                    wordBreak: 'break-all',
                  }}
                >
                  {email || 'No email address'}
                </motion.div>
              </AnimatePresence>
            </div>
            <Button
              type="button"
              onClick={() => setIsEditingEmail(true)}
              variant="secondary"
              size={isMobile ? 'sm' : 'md'}
              style={isMobile ? { alignSelf: 'flex-start' } : undefined}
            >
              Edit Info
            </Button>
          </div>
        )}
      </div>

      {/* Password Section */}
      <div
        style={{
          backgroundColor: colors.secondaryBg,
          border: `${isMobile ? '1px' : '2px'} solid ${colors.primaryBorder}`,
          borderRadius: isMobile ? '6px' : '8px',
          padding: isMobile ? '12px' : '18px',
          marginBottom: isMobile ? '14px' : '20px',
        }}
      >
        {!isChangingPassword ? (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? '12px' : '16px',
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: isMobile ? '11px' : '13px',
                  fontWeight: '600',
                  color: colors.secondaryText,
                  marginBottom: '4px',
                }}
              >
                Password
              </label>
              <div
                style={{
                  fontSize: isMobile ? '13px' : '15px',
                  color: colors.primaryText,
                  fontWeight: '500',
                }}
              >
                ••••••••
              </div>
            </div>
            <Button
              type="button"
              onClick={() => setIsChangingPassword(true)}
              variant="secondary"
              size={isMobile ? 'sm' : 'md'}
              style={isMobile ? { alignSelf: 'flex-start' } : undefined}
            >
              Change Password
            </Button>
          </div>
        ) : (
          <>
            <h3
              style={{
                fontSize: '15px',
                fontWeight: '700',
                marginBottom: '16px',
                color: colors.primaryText,
                letterSpacing: '-0.2px',
              }}
            >
              Change Password
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <Input
                id="currentPassword"
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword((e.target as HTMLInputElement).value)}
                placeholder="Enter current password"
                disabled={isLoading}
                type="password"
                autoComplete="current-password"
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <Input
                id="newPassword"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword((e.target as HTMLInputElement).value)}
                placeholder="Enter new password"
                disabled={isLoading}
                type="password"
                autoComplete="new-password"
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <Input
                id="confirmPassword"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword((e.target as HTMLInputElement).value)}
                placeholder="Confirm new password"
                disabled={isLoading}
                type="password"
                autoComplete="new-password"
              />
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '4px',
              }}
            >
              <Button
                type="button"
                onClick={() => {
                  setIsChangingPassword(false)
                  setCurrentPassword('')
                  setNewPassword('')
                  setConfirmPassword('')
                }}
                variant="secondary"
                size="md"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSavePassword}
                disabled={isLoading}
                isLoading={isLoading}
                variant="primary"
                size="md"
              >
                Save
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Terms of Service Link */}
      <div
        style={{
          textAlign: 'center',
          fontSize: isMobile ? '10px' : '12px',
          color: colors.secondaryText,
        }}
      >
        <button
          type="button"
          onClick={() => setShowTOSModal(true)}
          style={{
            background: 'none',
            border: 'none',
            color: colors.visualAccent1,
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: 'inherit',
            fontFamily: 'inherit',
            padding: 0,
          }}
        >
          Terms of Service
        </button>
      </div>
      </form>

      {/* TOS Modal */}
      {showTOSModal && <TOSModal onClose={() => setShowTOSModal(false)} />}
    </div>,
    document.body
  )
}
